import React, { useEffect } from 'react';

import { Carousel } from '@mantine/carousel';
import { Box, Button, Flex, Grid, Stack, TextInput, Title } from '@mantine/core';
import { useForm } from '@mantine/form';
import { DroxyCompactCard } from 'components/Droxy/DroxyCompactCard';
import { DroxyCompactSelectCard } from 'components/Droxy/DroxyCompactSelectCard';
import { DroxyDrawer } from 'components/Droxy/DroxyDrawer';
import { useSubscriptionContext } from 'contexts/SubscriptionContext';
import { useSmartAssistantCreationHandling } from 'hooks/use-smart-assistant-creation-handling';
import { AgentIcon } from 'icons/AgentIcon';
import { useTranslation } from 'react-i18next';
import { SmartAssistantType } from 'shared/interfaces/SmartAssistant';
import { requiredInputValidator } from 'shared/utils/form-validators';
import { getAgentGroups } from 'shared/utils/smart-assistant';
import {
  SmartAssistantTemplateId,
  createSmartAssistantDtoFromTemplate,
  getTemplatesForType
} from 'shared/utils/smart-assistant-templates';

interface CreateSmartAssistantDrawerProps {
  opened: boolean;
  onClose: () => void;
}

type CreateSmartAssistantFormValues = {
  name: string;
  type: SmartAssistantType;
  templateId: SmartAssistantTemplateId;
};

export function CreateSmartAssistantDrawer({ opened, onClose }: CreateSmartAssistantDrawerProps) {
  const { t } = useTranslation();
  const [subscription] = useSubscriptionContext();
  const { createSmartAssistantMutation } = useSmartAssistantCreationHandling();

  const createSmartAssistantForm = useForm<CreateSmartAssistantFormValues>({
    initialValues: { name: 'New Agent', type: SmartAssistantType.BASIC, templateId: 'basic-blank' },
    validate: {
      name: (value) => requiredInputValidator(value),
      type: (value) => requiredInputValidator(value),
      templateId: (value) => requiredInputValidator(value)
    }
  });

  const agentGroups = getAgentGroups(subscription);
  const smartAssistantTemplatesForType = getTemplatesForType(createSmartAssistantForm.values.type);
  const shouldShowTemplateSelection = smartAssistantTemplatesForType.length > 1;

  // When the type changes, we need to set the template to the first one
  useEffect(() => {
    const templatesForType = getTemplatesForType(createSmartAssistantForm.values.type);
    if (templatesForType.length > 0) {
      createSmartAssistantForm.setFieldValue('templateId', templatesForType[0].id);
    }
  }, [createSmartAssistantForm.values.type]);

  const handleSmartAssistantTemplateSlideChange = (index: number) => {
    if (smartAssistantTemplatesForType[index]) {
      const template = smartAssistantTemplatesForType[index];
      createSmartAssistantForm.setFieldValue('templateId', template.id);
      createSmartAssistantForm.setFieldValue('name', template.baseCreateSmartAssistantDto.customization.name);
    }
  };

  const createSmartAssistant = async () => {
    const validation = createSmartAssistantForm.validate();
    if (validation.hasErrors) return;

    const createSmartAssistantDto = createSmartAssistantDtoFromTemplate(
      createSmartAssistantForm.values.templateId,
      createSmartAssistantForm.values.name
    );

    await createSmartAssistantMutation.mutateAsync(createSmartAssistantDto);
    createSmartAssistantForm.reset();
    onClose();
  };

  return (
    <DroxyDrawer
      opened={opened}
      onClose={onClose}
      title={t('SMART_ASSISTANT.CREATE_SMART_ASSISTANT_DRAWER.TITLE')}
      icon={<AgentIcon w={25} h={25} />}
      description={t('SMART_ASSISTANT.CREATE_SMART_ASSISTANT_DRAWER.DESCRIPTION')}
      position="right">
      <Box px={24} style={{ flex: 1, height: '100%', overflow: 'auto' }}>
        <Stack spacing={18} justify="space-between" style={{ height: '100%' }}>
          <Stack spacing={24}>
            <Stack spacing={6}>
              <Title order={4}>{t('SMART_ASSISTANT.CREATE_SMART_ASSISTANT_DRAWER.AGENT_NAME.TITLE')}</Title>
              <TextInput
                placeholder={t('SMART_ASSISTANT.CREATE_SMART_ASSISTANT_DRAWER.AGENT_NAME.PLACEHOLDER')}
                {...createSmartAssistantForm.getInputProps('name')}
              />
            </Stack>

            <Stack spacing={16}>
              <Title order={4}>{t('SMART_ASSISTANT.CREATE_SMART_ASSISTANT_DRAWER.AGENT_TYPE.TITLE')}</Title>
              {agentGroups.map((group) => (
                <Stack key={group.title} spacing={12}>
                  <Title order={5} color="dimmed">
                    {group.title}
                  </Title>
                  <Grid gutter={12}>
                    {group.items.map((item) => (
                      <Grid.Col key={item.type} span={6}>
                        <DroxyCompactSelectCard
                          title={item.title}
                          description={item.description}
                          icon={item.icon}
                          isSelected={createSmartAssistantForm.values.type === item.type}
                          onSelect={() => createSmartAssistantForm.setFieldValue('type', item.type)}
                          disabled={!item.isAccessible}
                        />
                      </Grid.Col>
                    ))}
                  </Grid>
                </Stack>
              ))}
            </Stack>

            {shouldShowTemplateSelection && (
              <Stack spacing={16}>
                <Title order={4}>{t('SMART_ASSISTANT.CREATE_SMART_ASSISTANT_DRAWER.CHOOSE_TEMPLATE.TITLE')}</Title>
                <Carousel
                  key={createSmartAssistantForm.values.type}
                  slideSize="100%"
                  slideGap="md"
                  align="center"
                  withControls
                  controlsOffset="xs"
                  controlSize={28}
                  onSlideChange={handleSmartAssistantTemplateSlideChange}>
                  {smartAssistantTemplatesForType.map((template) => (
                    <Carousel.Slide key={template.id}>
                      <DroxyCompactCard
                        title={template.title}
                        description={template.description}
                        icon={template.icon}
                        style={{ maxWidth: '450px', margin: '0 auto' }}
                      />
                    </Carousel.Slide>
                  ))}
                </Carousel>
              </Stack>
            )}
          </Stack>

          <Flex justify="flex-end" pb={24}>
            <Button onClick={createSmartAssistant} loading={createSmartAssistantMutation.isLoading}>
              {t('SMART_ASSISTANT.CREATE_SMART_ASSISTANT_DRAWER.CREATE')}
            </Button>
          </Flex>
        </Stack>
      </Box>
    </DroxyDrawer>
  );
}
